<template>
  <div
    class="absolute top-0 p-2 text-white bg-gradient-to-r from-darkPurple to-darkBlue"
    style="width: 80%; height: auto; z-index: 99"
  >
    <section class="flex flex-row cursor-pointer">
      <button
        class="flex w-8 items-start justify-start"
        @click="close"
      >
        <icon
          :name="'mdi-close'"
          :size="20"
          :color="'#ffffff'"
        />
      </button>
      <div class="pt-3">
        <CustomerSummary
          :customer="customer"
          :active="true"
        />
      </div>
      <div class="flex flex-1 justify-end p-3 items-center pr-5">
        <CustomerCertificationHeader
          :customer="customer"
          class="self-end"
        />
        <div
          v-if="customer.certification?.validated_at"
          class="text-white text-sm ml-3"
        >
          <div>Patient Apodis</div>
          <div>Depuis le {{ validatedAt }}</div>
          <div v-if="customer.certification.phone">
            ({{ $filters.phoneFormat(customer.certification.phone) }})
          </div>
        </div>
      </div>
    </section>
    <section class="px-7 py-5 flex flex-col space-y-3">
      <div class="flex flex-row space-x-3">
        <CustomerInformationBlock
          label="Adresse"
          :value="address"
        />
        <CustomerInformationBlock
          label="Téléphone"
          :value="customer.information.phone || customer.information.mobile"
        />
        <CustomerInformationBlock
          label="Email"
          :value="customer.information.email"
        />
      </div>
      <div class="flex flex-row space-x-3">
        <div class="flex flex-1">
          <CustomerInformationBlock
            label="Caisse primaire"
            :value="customer.information.fund_primary"
          />
        </div>
        <div class="flex flex-2">
          <CustomerInformationBlock
            label="Caisse complémentaire"
            :value="customer.information.fund_complementary"
          />
        </div>
      </div>
      <div class="flex flex-row space-x-3">
        <CustomerInformationBlock
          label="Médecin traitant"
          :value="customer.last_prescriptor && customer.last_prescriptor.name"
        />
      </div>
      <div class="flex flex-row space-x-3">
        <CustomerInformationBlock
          label="Groupe sanguin"
          :value="customer.user_information?.blood_group"
        />
        <div class="flex flex-1 flex-col">
          <h2 class="text-white">
            Poids (kg)
          </h2>
          <input
            v-model.number="weight"
            type="number"
            min="0"
            class="text-sm bg-transparent border border-white/40 rounded px-1 py-0.5 w-20 placeholder:text-white/50"
            :placeholder="customer.user_information?.weight ?? '_'"
            :disabled="saving"
            @change="saveDeclaredInformation('weight', weight === '' ? null : weight)"
          >
        </div>
        <div class="flex flex-1 flex-col">
          <h2 class="text-white">
            Taille (cm)
          </h2>
          <input
            v-model.number="height"
            type="number"
            min="0"
            class="text-sm bg-transparent border border-white/40 rounded px-1 py-0.5 w-20 placeholder:text-white/50"
            :placeholder="customer.user_information?.height ?? '_'"
            :disabled="saving"
            @change="saveDeclaredInformation('height', height === '' ? null : height)"
          >
        </div>
        <div class="flex flex-1 flex-col">
          <h2 class="text-white">
            Fumeur
          </h2>
          <select
            v-model="smoking"
            class="text-sm bg-transparent border border-white/40 rounded px-1 py-0.5 w-fit"
            :disabled="saving"
            @change="saveDeclaredInformation('smoking', smoking)"
          >
            <option
              v-for="option in smokingOptions"
              :key="String(option.value)"
              :value="option.value"
              class="text-gray-900"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import CustomerSummary from '@/customer/components/customer-summary/CustomerSummary.vue';
import CustomerCertificationHeader from '@/customer-certification/components/CustomerCertificationHeader.vue';
import dateHelper from '@/core/helpers/dateHelper';
import CustomerInformationBlock from '@/customer/components/customer-information/CustomerInformationBlock.vue';
import Icon from '@/core/components/icon/Icon.vue';
import {customerApi} from '@/container';
import {usePatientStore} from '@/stores/patient.store';
import {mapActions} from 'pinia';
import {CustomerDeclaredInformation, SmokingStatus} from '@/customer/interfaces/DeclaredInformation';

export default defineComponent({
    name: 'CustomerInformationModal',
    components: { CustomerInformationBlock, CustomerCertificationHeader, CustomerSummary, Icon },
    props: {
        customer: {
            type: Object as PropType<any>,
            required: true
        }
    },

    data() {
        return {
            smoking: (this.customer.declared_information?.smoking ?? null) as SmokingStatus | null,
            weight: (this.customer.declared_information?.weight ?? '') as number | '',
            height: (this.customer.declared_information?.height ?? '') as number | '',
            saving: false,
            smokingOptions: [
                {value: null, label: 'Non renseigné'},
                {value: SmokingStatus.SMOKER, label: 'Fumeur'},
                {value: SmokingStatus.NON_SMOKER, label: 'Non-fumeur'},
                {value: SmokingStatus.OCCASIONAL_SMOKER, label: 'Fumeur occasionnel'}
            ] as {value: SmokingStatus | null, label: string}[]
        };
    },

    computed: {
        validatedAt () {
            return this.customer.certification && dateHelper.shortFormat(new Date(this.customer.certification.validated_at));
        },
        address () {
            const address = (this.customer.information.address_street || '') + ', ' +
        ((this.customer.information.postal_code && this.customer.information.postal_code.replace(' ', '')) || '') +
        ' ' + (this.customer.information.city || '');

            return address.replace(' ', '').length > 2 ? address : undefined;
        }
    },

    methods: {
        ...mapActions(usePatientStore, ['refreshCustomer']),
        close () {
            window.dispatchEvent(new Event('information-closed'));
        },
        /**
         * Persist a declared information field right away and refresh the customer data.
         */
        async saveDeclaredInformation(field: keyof CustomerDeclaredInformation, value: CustomerDeclaredInformation[keyof CustomerDeclaredInformation]) {
            this.saving = true;

            try {
                await customerApi().patch(this.customer.id, {[field]: value});
                await this.refreshCustomer();
            } finally {
                this.saving = false;
            }
        }
    }
});
</script>

<style scoped>
/* Plain edit fields: hide the number input spin buttons. */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>
